import { PageIndices, ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, ContentCopy, Delete, Edit } from '@mui/icons-material';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';

import { DashboardLayout } from '@/components/DashboardLayout';
import { PageCard } from '@/components/common/PageCard';
import { PageHint } from '@/components/common/PageHint';
import { PageToolbar } from '@/components/common/PageToolbar';
import { BaseDialog } from '@/components/common/BaseDialog';
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog.tsx';
import { UniversalPagination } from '@/components/common/UniversalPagination';
import { toastService } from '@/services/toast.service';
import {
  expertPromptsService,
  ExpertPrompt,
  CreateExpertPromptDto,
} from '@/services/expert-prompts.service';

const TITLE_MAX = 255;
const CONTENT_MAX = 20000;

function emptyForm(): CreateExpertPromptDto {
  return { title: '', content: '' };
}

export const ExpertPromptsPage: React.FC = () => {
  const [prompts, setPrompts] = useState<ExpertPrompt[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // Форма (create / edit)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<ExpertPrompt | null>(null);
  const [form, setForm] = useState<CreateExpertPromptDto>(emptyForm());
  const [saving, setSaving] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<ExpertPrompt | null>(null);

  // Detail view
  const [detailPrompt, setDetailPrompt] = useState<ExpertPrompt | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const result = await expertPromptsService.getAll(page, limit, search || undefined);
      setPrompts(result.data);
      setTotal(result.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити промпти');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // ── Відкрити форму ────────────────────────────────────────────────────────
  const handleOpenCreate = () => {
    setEditingPrompt(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const handleOpenEdit = (prompt: ExpertPrompt) => {
    setEditingPrompt(prompt);
    setForm({ title: prompt.title, content: prompt.content });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  // ── Зберегти ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      if (editingPrompt) {
        await expertPromptsService.update(editingPrompt.id, form);
        toastService.success('Промпт оновлено');
      } else {
        await expertPromptsService.create(form);
        toastService.success('Промпт створено');
      }
      setDialogOpen(false);
      setPage(1);
      fetchPrompts();
    } catch {
      toastService.error('Не вдалося зберегти промпт');
    } finally {
      setSaving(false);
    }
  };

  // ── Видалити ──────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await expertPromptsService.remove(deleteTarget.id);
      toastService.success('Промпт видалено');
      setDeleteTarget(null);
      fetchPrompts();
    } catch {
      toastService.error('Не вдалося видалити промпт');
    }
  };

  const handleCopyContent = () => {
    if (!detailPrompt) return;
    navigator.clipboard.writeText(detailPrompt.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const isFormValid = form.title.trim().length > 0 && form.content.trim().length > 0;

  return (
    <DashboardLayout>
      <Box data-i-doc={PageIndices.EXPERT_PROMPTS}>
        <PageHint iDoc={PageIndices.EXPERT_PROMPTS} />

        <PageCard>
          <PageToolbar
            storageKey="filters:expert-prompts"
            filters={[
              {
                id: 'search',
                type: 'text',
                label: 'Пошук за назвою',
                value: search,
                onChange: (v) => { setSearch(v); setPage(1); },
              },
            ]}
            onReset={() => { setSearch(''); setPage(1); }}
            extraActions={
              <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
                Додати
              </Button>
            }
          />

          {loading && prompts.length === 0 ? (
            <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : prompts.length === 0 ? (
            <Typography sx={{ mt: 4, textAlign: 'center' }} color="text.secondary">
              {search ? 'Нічого не знайдено' : 'Промптів ще немає. Натисніть «Додати», щоб створити перший.'}
            </Typography>
          ) : (
            <List disablePadding>
              {prompts.map((prompt) => (
                <ListItem key={prompt.id} divider disablePadding alignItems="flex-start">
                  <ListItemButton
                    onClick={() => { setDetailPrompt(prompt); setCopied(false); }}
                    sx={{ pr: 10 }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight={500}>
                          {prompt.title}
                        </Typography>
                      }
                      secondary={
                        <Box component="span">
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {prompt.content}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.disabled"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {format(new Date(prompt.createdAt), 'd MMM yyyy', { locale: uk })}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                  <ListItemSecondaryAction>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleOpenEdit(prompt); }}
                      disabled={loading}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(prompt); }}
                      disabled={loading}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          <UniversalPagination
            page={page}
            count={Math.ceil(total / limit)}
            limit={limit}
            total={total}
            onPageChange={setPage}
            onLimitChange={(l) => { setLimit(l); setPage(1); }}
            storageKey="pagination:expert-prompts"
          />
        </PageCard>
      </Box>

      {/* Деталі промпту */}
      <BaseDialog
        open={!!detailPrompt}
        onClose={() => setDetailPrompt(null)}
        maxWidth="sm"
        iDoc={ModalIndices.EXPERT_PROMPT_DETAIL}
        title={detailPrompt?.title ?? ''}
        actions={
          <Box display="flex" gap={1} sx={{ mr: 'auto' }}>
            <Tooltip title={copied ? 'Скопійовано!' : 'Копіювати текст промпту'}>
              <Button
                size="small"
                startIcon={<ContentCopy fontSize="small" />}
                onClick={handleCopyContent}
                color={copied ? 'success' : 'inherit'}
              >
                {copied ? 'Скопійовано' : 'Копіювати'}
              </Button>
            </Tooltip>
            <Button
              size="small"
              startIcon={<Edit fontSize="small" />}
              onClick={() => {
                if (detailPrompt) {
                  handleOpenEdit(detailPrompt);
                  setDetailPrompt(null);
                }
              }}
            >
              Редагувати
            </Button>
          </Box>
        }
      >
        <Box>
          <Typography variant="caption" color="text.disabled">
            Створено {detailPrompt && format(new Date(detailPrompt.createdAt), 'd MMMM yyyy, HH:mm', { locale: uk })}
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.75, color: 'text.primary' }}
          >
            {detailPrompt?.content}
          </Typography>
        </Box>
      </BaseDialog>

      {/* Діалог створення / редагування */}
      <BaseDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        iDoc={ModalIndices.EXPERT_PROMPT_FORM}
        title={editingPrompt ? 'Редагувати промпт' : 'Новий промпт'}
        actions={
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isFormValid || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Збереження...' : 'Зберегти'}
          </Button>
        }
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Назва"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            fullWidth
            autoFocus
            inputProps={{ maxLength: TITLE_MAX }}
            helperText={`${form.title.length} / ${TITLE_MAX}`}
          />
          <TextField
            label="Текст промпту"
            value={form.content}
            onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
            fullWidth
            multiline
            minRows={6}
            maxRows={16}
            inputProps={{ maxLength: CONTENT_MAX }}
            helperText={`${form.content.length} / ${CONTENT_MAX}`}
          />
        </Box>
      </BaseDialog>

      {/* Підтвердження видалення */}
      <ConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Видалити промпт"
        message={`Видалити промпт «${deleteTarget?.title}»? Скасувати дію буде неможливо.`}
        confirmText="Видалити"
      />
    </DashboardLayout>
  );
};
